import {Component, forwardRef, Host, OnInit, Optional, SkipSelf, ViewChild} from '@angular/core';
import {MatSelect, MatSelectChange} from '@angular/material/select';
import {ControlContainer, FormControl, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';

import {ComponentTypesEnum} from '../../enums';
import {BaseComponent, SelectControl} from '../../classes';

import {SelectOptionInterface} from './interfaces/select-option.interface';
import {WhitespaceValidator} from '@spa/validators';

type SelectValueType = number | string | boolean | number[] | string[];

@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SelectComponent),
        multi: true
    }]
})
export class SelectComponent extends BaseComponent<SelectValueType, MatSelectChange, SelectControl> implements OnInit {

    @ViewChild('select', {static: true}) public selectReference: MatSelect;
    inputTxt: string;
    inputForm: FormGroup;

    constructor(
        @Optional() @Host() @SkipSelf()
        protected controlContainer: ControlContainer
    ) {
        super(controlContainer);
    }

    ngOnInit() {
        this.asFormsControl();
        this.checkInvalid();

        if (this.asFormsControl().isDynamic) {
            this.inputForm = new FormGroup({
                inputTxt: new FormControl(this.inputTxt, WhitespaceValidator())
            });
        }
    }

    handleCreateNewItemClick() {
        const inputControl = this.inputForm.controls.inputTxt;
        if (!inputControl.valid) {
            return;
        }

        this.asFormsControl().options.push({
            label: inputControl.value,
            value: inputControl.value,
        });

        inputControl.reset(null);
    }

    changeValue(event: MatSelectChange): void {
        this.onChange(event.value);
        this.checkInvalid();
        this.control.resetErrors();

        super.writeValue(event.value);
    }

    writeValue(value: SelectValueType): void {
        if (!Array.isArray(value)) {
            const filteredOptions = this.asFormsControl().options.filter((option: SelectOptionInterface) => {
                return value === option.value;
            });

            if (filteredOptions.length !== 0) {
                this.selectReference.value = filteredOptions[0].value;
                super.writeValue(filteredOptions[0].value);
            } else {
                switch (typeof value) {
                    case 'string':
                        this.selectReference.value = '';
                        super.writeValue('');
                        break;
                    case 'boolean':
                        this.selectReference.value = (value !== true) ? null : value;
                        super.writeValue((value !== true) ? null : value);
                        break;
                    default:
                        this.selectReference.value = null;
                        super.writeValue(null);
                }
            }
        } else {
            if (value.length > 0) {
                this.selectReference.value = value;
                super.writeValue(value);
            } else {
                this.selectReference.value = [];
                super.writeValue([]);
            }
        }

        this.checkInvalid();
        this.control.resetErrors();
    }

    asFormsControl(): SelectControl {
        if (!this.control || !(this.control instanceof SelectControl)) {
            this.control = new SelectControl({
                name: '',
                value: '',
                placeholder: '',
                validators: [],
                componentType: ComponentTypesEnum.Select,
                invalid: false,
                errors: [],
                options: [],
                multiple: false
            });
        }

        return (this.control as SelectControl);
    }
}
